import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [payments, setPayments] = useState([]);
    const [inviteCodes, setInviteCodes] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [systemHealth, setSystemHealth] = useState(null);
    const [tokenAnalytics, setTokenAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [searchTerm, setSearchTerm] = useState('');
    const [userFilter, setUserFilter] = useState('all');
    const [refreshing, setRefreshing] = useState(false);
    const [notification, setNotification] = useState(null);

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const fetchAdminData = useCallback(async () => {
        try {
            setRefreshing(true);
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            const [statsRes, usersRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/admin/stats`, { headers }),
                axios.get(`${API_BASE_URL}/api/admin/users`, { headers })
            ]);
            
            setStats(statsRes.data);
            setUsers(usersRes.data);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch admin data:", err);
            setError(err.response?.data?.detail || "Failed to load dashboard data");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    const fetchInviteCodes = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${API_BASE_URL}/api/admin/invite-codes`, { headers });
            setInviteCodes(response.data);
        } catch (err) {
            console.error("Failed to fetch invite codes:", err);
        }
    }, []);

    const fetchPayments = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${API_BASE_URL}/api/admin/payments`, { headers });
            setPayments(response.data);
        } catch (err) {
            console.error("Failed to fetch payments:", err);
        }
    }, []);

    const fetchAnalytics = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const [analyticsRes, healthRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/admin/subscription-analytics`, { headers }),
                axios.get(`${API_BASE_URL}/api/admin/system-health`, { headers })
            ]);
            setAnalytics(analyticsRes.data);
            setSystemHealth(healthRes.data);
        } catch (err) {
            console.error("Failed to fetch analytics:", err);
        }
    }, []);

    const fetchTokenAnalytics = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${API_BASE_URL}/api/admin/token-usage-analytics`, { headers });
            setTokenAnalytics(response.data);
        } catch (err) {
            console.error("Failed to fetch token analytics:", err);
        }
    }, []);

    useEffect(() => {
        fetchAdminData();
        if (activeTab === 'invites') {
            fetchInviteCodes();
        } else if (activeTab === 'payments') {
            fetchPayments();
        } else if (activeTab === 'analytics') {
            fetchAnalytics();
        } else if (activeTab === 'tokens') {
            fetchTokenAnalytics();
        }
    }, [fetchAdminData, fetchInviteCodes, fetchPayments, fetchAnalytics, fetchTokenAnalytics, activeTab]);

    // Auto-refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            if (activeTab === 'dashboard') {
                fetchAdminData();
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [fetchAdminData, activeTab]);

    const handleUserAction = async (userId, action) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            if (action === 'toggle_active') {
                await axios.patch(`${API_BASE_URL}/api/admin/users/${userId}/toggle-active`, {}, { headers });
            }
            
            // Refresh user data
            fetchAdminData();
            showNotification(`User ${action} successfully`);
        } catch (err) {
            console.error(`Failed to ${action} user:`, err);
            showNotification(`Failed to ${action} user: ${err.response?.data?.detail || err.message}`, 'error');
        }
    };

    const createInviteCode = async (maxUses = 1, expiresInDays = 30) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + expiresInDays);
            
            await axios.post(`${API_BASE_URL}/api/admin/invite-codes`, {
                max_uses: maxUses,
                expires_at: expiresAt.toISOString()
            }, { headers });
            
            fetchInviteCodes();
            showNotification('Invite code created successfully');
        } catch (err) {
            console.error("Failed to create invite code:", err);
            showNotification(`Failed to create invite code: ${err.response?.data?.detail || err.message}`, 'error');
        }
    };

    const revokeInviteCode = async (code) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            await axios.delete(`${API_BASE_URL}/api/admin/invite-codes/${code}`, { headers });
            fetchInviteCodes();
            showNotification('Invite code revoked successfully');
        } catch (err) {
            console.error("Failed to revoke invite code:", err);
            showNotification(`Failed to revoke invite code: ${err.response?.data?.detail || err.message}`, 'error');
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (user.full_name || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = userFilter === 'all' || user.subscription_plan === userFilter;
        return matchesSearch && matchesFilter;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center">
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg inline-block">
                    <p className="font-bold">Error</p>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            {/* Notification Toast */}
            {notification && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg transition-all duration-300 ${
                    notification.type === 'error' 
                        ? 'bg-red-500 text-white' 
                        : 'bg-green-500 text-white'
                }`}>
                    <div className="flex items-center">
                        <span className="mr-2">
                            {notification.type === 'error' ? '❌' : '✅'}
                        </span>
                        {notification.message}
                    </div>
                </div>
            )}
            
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                            <p className="text-gray-500 text-sm">Platform management and analytics</p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={fetchAdminData}
                                disabled={refreshing}
                                className="flex items-center px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {refreshing ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                ) : (
                                    <span className="mr-2">🔄</span>
                                )}
                                Refresh
                            </button>
                        </div>
                    </div>
                </header>

                {/* Navigation Tabs */}
                <nav className="bg-white border-b border-gray-200 px-6">
                    <div className="flex space-x-8">
                        {[
                            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
                            { id: 'users', label: 'Users', icon: '👥' },
                            { id: 'payments', label: 'Payments', icon: '💳' },
                            { id: 'tokens', label: 'Token Analytics', icon: '🔢' },
                            { id: 'analytics', label: 'Analytics', icon: '📈' },
                            { id: 'invites', label: 'Invite Codes', icon: '🎫' },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center px-3 py-4 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === tab.id
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <span className="mr-2">{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </nav>

                {/* Content */}
                <div className="p-6">
                    {activeTab === 'dashboard' && (
                        <DashboardTab stats={stats} loading={loading} error={error} />
                    )}
                    {activeTab === 'users' && (
                        <UsersTab 
                            users={filteredUsers}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            userFilter={userFilter}
                            setUserFilter={setUserFilter}
                            onUserAction={handleUserAction}
                            loading={loading}
                            error={error}
                        />
                    )}
                    {activeTab === 'payments' && (
                        <PaymentsTab payments={payments} />
                    )}
                    {activeTab === 'tokens' && (
                        <TokenAnalyticsTab tokenAnalytics={tokenAnalytics} />
                    )}
                    {activeTab === 'analytics' && (
                        <AnalyticsTab analytics={analytics} systemHealth={systemHealth} />
                    )}
                    {activeTab === 'invites' && (
                        <InviteCodesTab 
                            inviteCodes={inviteCodes}
                            onCreateInvite={createInviteCode}
                            onRevokeInvite={revokeInviteCode}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ title, value, subValue, icon, color }) => {
    const colorClasses = {
        blue: 'text-blue-600 bg-blue-50',
        green: 'text-green-600 bg-green-50',
        purple: 'text-purple-600 bg-purple-50',
        indigo: 'text-indigo-600 bg-indigo-50',
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex justify-between items-start mb-4">
                <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
                    <span className="text-xl">{icon}</span>
                </div>
            </div>
            <h3 className="text-gray-500 text-sm font-medium mb-1 uppercase tracking-wider">{title}</h3>
            <div className="text-2xl font-black text-gray-900">{value}</div>
            <div className="text-sm text-gray-400 mt-1">{subValue}</div>
        </div>
    );
};

export default AdminDashboard;

// Dashboard Tab Component
const DashboardTab = ({ stats, loading, error }) => {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg inline-block">
                    <p className="font-bold">Error</p>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Total Users"
                    value={stats.users.total}
                    subValue={`${stats.users.active} active`}
                    icon="👥"
                    color="blue"
                />
                <StatCard
                    title="Revenue"
                    value={`₹${stats.revenue.total_inr.toLocaleString()}`}
                    subValue="All time"
                    icon="💰"
                    color="green"
                />
                <StatCard
                    title="Total Tokens"
                    value={stats.tokens.total.toLocaleString()}
                    subValue={`${stats.tokens.total_input.toLocaleString()} in / ${stats.tokens.total_output.toLocaleString()} out`}
                    icon="🤖"
                    color="purple"
                />
                <StatCard
                    title="LLM Requests"
                    value={stats.tokens.by_model.reduce((acc, curr) => acc + curr.requests, 0)}
                    subValue="Across all models"
                    icon="⚡"
                    color="indigo"
                />
            </div>

            {/* Model Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">LLM Usage by Model</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {stats.tokens.by_model.map((model, idx) => (
                        <div key={idx} className="bg-gray-50 px-4 py-3 rounded-lg border border-gray-100">
                            <div className="flex justify-between items-start mb-2">
                                <span className="text-sm font-semibold text-gray-600">{model.model || 'Unknown'}</span>
                                <span className="text-xs text-gray-400">{model.requests} requests</span>
                            </div>
                            <div className="text-lg font-bold text-gray-800">{model.tokens.toLocaleString()}</div>
                            <div className="text-xs text-gray-500">tokens used</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Users Tab Component
const UsersTab = ({ users, searchTerm, setSearchTerm, userFilter, setUserFilter, onUserAction, loading, error }) => {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg inline-block">
                    <p className="font-bold">Error</p>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Search and Filter */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Search users by email or name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>
                    <div>
                        <select
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="all">All Plans</option>
                            <option value="FREE">Free</option>
                            <option value="PRO">Pro</option>
                            <option value="PREMIUM">Premium</option>
                        </select>
                    </div>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                    Showing {users.length} users
                </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-500 uppercase text-xs font-semibold">
                            <tr>
                                <th className="px-6 py-4">User</th>
                                <th className="px-6 py-4">Plan</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Notebooks</th>
                                <th className="px-6 py-4">Tokens Used</th>
                                <th className="px-6 py-4">Joined</th>
                                <th className="px-6 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {users.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-gray-800">{user.full_name || 'No Name'}</span>
                                            <span className="text-sm text-gray-500">{user.email}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                                            user.subscription_plan === 'PREMIUM' ? 'bg-purple-100 text-purple-700' :
                                            user.subscription_plan === 'PRO' ? 'bg-indigo-100 text-indigo-700' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {user.subscription_plan}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            user.subscription_status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                            {user.subscription_status || 'INACTIVE'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-gray-700">
                                        {user.stats.notebooks}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-gray-800">{user.stats.total_tokens.toLocaleString()}</span>
                                            <span className="text-xs text-gray-500">
                                                In: {user.stats.total_input_tokens.toLocaleString()} |
                                                Out: {user.stats.total_output_tokens.toLocaleString()}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-gray-500 text-sm">
                                        {new Date(user.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4">
                                        <button
                                            onClick={() => onUserAction(user.id, 'toggle_active')}
                                            className="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                                        >
                                            Toggle Active
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// Invite Codes Tab Component
const InviteCodesTab = ({ inviteCodes, onCreateInvite, onRevokeInvite }) => {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [maxUses, setMaxUses] = useState(1);
    const [expiresInDays, setExpiresInDays] = useState(30);

    const handleCreateInvite = () => {
        onCreateInvite(maxUses, expiresInDays);
        setShowCreateForm(false);
        setMaxUses(1);
        setExpiresInDays(30);
    };

    return (
        <div className="space-y-6">
            {/* Create Invite Code */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Invite Code Management</h3>
                    <button
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                    >
                        {showCreateForm ? 'Cancel' : 'Create New Code'}
                    </button>
                </div>

                {showCreateForm && (
                    <div className="border-t pt-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={maxUses}
                                    onChange={(e) => setMaxUses(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Expires in (days)</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={expiresInDays}
                                    onChange={(e) => setExpiresInDays(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>
                        <button
                            onClick={handleCreateInvite}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                            Create Invite Code
                        </button>
                    </div>
                )}
            </div>

            {/* Invite Codes List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800">Active Invite Codes</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-500 uppercase text-xs font-semibold">
                            <tr>
                                <th className="px-6 py-4">Code</th>
                                <th className="px-6 py-4">Max Uses</th>
                                <th className="px-6 py-4">Used</th>
                                <th className="px-6 py-4">Expires</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {inviteCodes.map((code) => (
                                <tr key={code.code} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 font-mono text-sm bg-gray-50 rounded">
                                        {code.code}
                                    </td>
                                    <td className="px-6 py-4">{code.max_uses}</td>
                                    <td className="px-6 py-4">{code.used_count}</td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {new Date(code.expires_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            code.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                        }`}>
                                            {code.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        {code.is_active && (
                                            <button
                                                onClick={() => onRevokeInvite(code.code)}
                                                className="text-sm px-3 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700 transition"
                                            >
                                                Revoke
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// Payments Tab Component
const PaymentsTab = ({ payments }) => {
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const filteredPayments = payments.filter(payment => {
        const matchesSearch = payment.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (payment.user_name || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const totalRevenue = payments
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + p.amount_inr, 0);

    return (
        <div className="space-y-6">
            {/* Payment Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <span className="text-xl">💰</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                            <p className="text-2xl font-bold text-gray-900">₹{totalRevenue.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <span className="text-xl">📊</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Total Payments</p>
                            <p className="text-2xl font-bold text-gray-900">{payments.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <span className="text-xl">✅</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Successful Payments</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {payments.filter(p => p.status === 'completed').length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Search payments by user email or name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>
                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="all">All Status</option>
                            <option value="completed">Completed</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                        </select>
                    </div>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                    Showing {filteredPayments.length} payments
                </div>
            </div>

            {/* Payments Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-500 uppercase text-xs font-semibold">
                            <tr>
                                <th className="px-6 py-4">User</th>
                                <th className="px-6 py-4">Amount</th>
                                <th className="px-6 py-4">Plan</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Payment ID</th>
                                <th className="px-6 py-4">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredPayments.map((payment) => (
                                <tr key={payment.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-gray-800">{payment.user_name || 'No Name'}</span>
                                            <span className="text-sm text-gray-500">{payment.user_email}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="font-bold text-gray-800">₹{payment.amount_inr.toLocaleString()}</span>
                                        <span className="text-xs text-gray-500 block">{payment.currency}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                                            payment.subscription_plan === 'PREMIUM' ? 'bg-purple-100 text-purple-700' :
                                            payment.subscription_plan === 'PRO' ? 'bg-indigo-100 text-indigo-700' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {payment.subscription_plan}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                                            payment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                            {payment.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="font-mono text-xs text-gray-600">
                                            {payment.razorpay_payment_id || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-gray-500 text-sm">
                                        {new Date(payment.created_at).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
// Analytics Tab Component
const AnalyticsTab = ({ analytics, systemHealth }) => {
    if (!analytics || !systemHealth) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* System Health */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">System Health (Last 24 Hours)</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{systemHealth.recent_activity.new_users_24h}</div>
                        <div className="text-sm text-gray-500">New Users</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{systemHealth.recent_activity.new_notebooks_24h}</div>
                        <div className="text-sm text-gray-500">New Notebooks</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{systemHealth.recent_activity.new_messages_24h}</div>
                        <div className="text-sm text-gray-500">New Messages</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-indigo-600">{systemHealth.recent_activity.new_payments_24h}</div>
                        <div className="text-sm text-gray-500">New Payments</div>
                    </div>
                </div>
            </div>

            {/* Error Metrics */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Error Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                        <div className={`text-2xl font-bold ${
                            systemHealth.error_metrics.payment_error_rate > 5 ? 'text-red-600' : 
                            systemHealth.error_metrics.payment_error_rate > 2 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                            {systemHealth.error_metrics.payment_error_rate}%
                        </div>
                        <div className="text-sm text-gray-500">Payment Error Rate</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{systemHealth.error_metrics.failed_payments}</div>
                        <div className="text-sm text-gray-500">Failed Payments</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-gray-600">{systemHealth.error_metrics.total_payments}</div>
                        <div className="text-sm text-gray-500">Total Payments</div>
                    </div>
                </div>
            </div>

            {/* Subscription Plan Distribution */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Subscription Plan Distribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {analytics.plan_distribution.map((plan, idx) => (
                        <div key={idx} className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className={`text-2xl font-bold ${
                                plan.plan === 'PREMIUM' ? 'text-purple-600' :
                                plan.plan === 'PRO' ? 'text-indigo-600' : 'text-gray-600'
                            }`}>
                                {plan.user_count}
                            </div>
                            <div className="text-sm text-gray-500">{plan.plan} Users</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Revenue by Plan */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Revenue by Plan</h3>
                {analytics.revenue_by_plan.length > 0 ? (
                    <div className="space-y-4">
                        {analytics.revenue_by_plan.map((plan, idx) => (
                            <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                                <div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                                        plan.plan === 'PREMIUM' ? 'bg-purple-100 text-purple-700' :
                                        plan.plan === 'PRO' ? 'bg-indigo-100 text-indigo-700' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                        {plan.plan}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-bold text-gray-800">₹{plan.revenue_inr.toLocaleString()}</div>
                                    <div className="text-sm text-gray-500">{plan.payment_count} payments</div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        No revenue data available
                    </div>
                )}
            </div>

            {/* Monthly Revenue Trend */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Monthly Revenue Trend</h3>
                {analytics.monthly_revenue.length > 0 ? (
                    <div className="space-y-2">
                        {analytics.monthly_revenue.map((month, idx) => (
                            <div key={idx} className="flex justify-between items-center p-3 hover:bg-gray-50 rounded">
                                <span className="text-gray-600">{month.month}</span>
                                <span className="font-semibold text-gray-800">₹{month.revenue_inr.toLocaleString()}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        No revenue data available for the last 6 months
                    </div>
                )}
            </div>

            {/* Top Users by Token Usage */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Users by Token Usage</h3>
                {systemHealth.top_users.length > 0 ? (
                    <div className="space-y-3">
                        {systemHealth.top_users.map((user, idx) => (
                            <div key={idx} className="flex justify-between items-center p-3 hover:bg-gray-50 rounded">
                                <div>
                                    <div className="font-medium text-gray-800">{user.name}</div>
                                    <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-bold text-gray-800">{user.total_tokens.toLocaleString()}</div>
                                    <div className="text-xs text-gray-500">tokens</div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        No token usage data available
                    </div>
                )}
            </div>
        </div>
    );
};
// Token Analytics Tab Component
const TokenAnalyticsTab = ({ tokenAnalytics }) => {
    if (!tokenAnalytics) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Current Month Overview */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Current Month Token Usage</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                            {tokenAnalytics.current_month.input_tokens.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">Input Tokens</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                            {tokenAnalytics.current_month.output_tokens.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">Output Tokens</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">
                            {tokenAnalytics.current_month.total_tokens.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">Total Tokens</div>
                    </div>
                </div>
            </div>

            {/* Model Usage Current Month */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Token Usage by Model (Current Month)</h3>
                {tokenAnalytics.model_usage_current_month.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {tokenAnalytics.model_usage_current_month.map((model, idx) => (
                            <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-sm font-semibold text-gray-600">{model.model}</span>
                                    <span className="text-xs text-gray-400">{model.unique_users} users</span>
                                </div>
                                <div className="space-y-1">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Total:</span>
                                        <span className="font-medium">{model.total_tokens.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-gray-400">
                                        <span>In: {model.input_tokens.toLocaleString()}</span>
                                        <span>Out: {model.output_tokens.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        No token usage data available for current month
                    </div>
                )}
            </div>

            {/* Monthly Trend */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Monthly Token Usage Trend</h3>
                {tokenAnalytics.monthly_trend.length > 0 ? (
                    <div className="space-y-3">
                        {tokenAnalytics.monthly_trend.map((month, idx) => (
                            <div key={idx} className="flex justify-between items-center p-3 hover:bg-gray-50 rounded">
                                <div>
                                    <span className="font-medium text-gray-800">{month.month}</span>
                                    <span className="text-sm text-gray-500 ml-2">({month.active_users} active users)</span>
                                </div>
                                <span className="font-bold text-gray-800">{month.total_tokens.toLocaleString()} tokens</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        No monthly trend data available
                    </div>
                )}
            </div>

            {/* Top Users Current Month */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Token Users (Current Month)</h3>
                {tokenAnalytics.top_users_current_month.length > 0 ? (
                    <div className="space-y-3">
                        {tokenAnalytics.top_users_current_month.map((user, idx) => (
                            <div key={idx} className="flex justify-between items-center p-3 hover:bg-gray-50 rounded">
                                <div className="flex items-center space-x-3">
                                    <div className="text-sm font-medium text-gray-400">#{idx + 1}</div>
                                    <div>
                                        <div className="font-medium text-gray-800">{user.name}</div>
                                        <div className="text-sm text-gray-500">{user.email}</div>
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                                        user.subscription_plan === 'PREMIUM' ? 'bg-purple-100 text-purple-700' :
                                        user.subscription_plan === 'PRO' ? 'bg-indigo-100 text-indigo-700' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                        {user.subscription_plan}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <div className="font-bold text-gray-800">{user.total_tokens.toLocaleString()}</div>
                                    <div className="text-xs text-gray-500">tokens</div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        No user token usage data available for current month
                    </div>
                )}
            </div>

            {/* Token Usage Insights */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Token Usage Insights</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-medium text-gray-700 mb-2">Usage Distribution</h4>
                        <div className="space-y-2">
                            {tokenAnalytics.model_usage_current_month.map((model, idx) => {
                                const totalTokens = tokenAnalytics.current_month.total_tokens;
                                const percentage = totalTokens > 0 ? ((model.total_tokens / totalTokens) * 100).toFixed(1) : 0;
                                return (
                                    <div key={idx} className="flex justify-between text-sm">
                                        <span className="text-gray-600">{model.model}</span>
                                        <span className="font-medium">{percentage}%</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <h4 className="font-medium text-gray-700 mb-2">Efficiency Metrics</h4>
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Avg tokens per user</span>
                                <span className="font-medium">
                                    {tokenAnalytics.top_users_current_month.length > 0 
                                        ? Math.round(tokenAnalytics.current_month.total_tokens / tokenAnalytics.top_users_current_month.length).toLocaleString()
                                        : 0
                                    }
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Input/Output ratio</span>
                                <span className="font-medium">
                                    {tokenAnalytics.current_month.output_tokens > 0 
                                        ? (tokenAnalytics.current_month.input_tokens / tokenAnalytics.current_month.output_tokens).toFixed(2)
                                        : 'N/A'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};